/*
 * $Id:: index.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { NOTIFICATIONS_SLICE_NAME } from '../../../../../app/constants';
import CustomPagination from '../../../../../components/Pagination/CustomPagination';
import WarningModal from '../../../../../components/WarningModal';
import { t, bareDateTimeFormat } from '../../../../../providers';
import { me as meSlice } from '../../../../AuthenticatedPage/authenticationSlice';
import {
    childAccounts as childAccountsSlice,
    loading as loadingSlice,
    selectedChildAccount as childAccountSlice,
    requestResult,
    error as errorSlice,
    loadingAccountLockEditChild as loadingAccountLockEditChildSlice,
    accountLocked as accountLockedSlice,
} from '../../../accountSlice';
import { SLICE_NAME } from '../../../constants';
import ActionsButton from './ActionsButton';
import ConfirmStatusChangeDialog from './ConfirmStatusChangeDialog';
import TableSortHead from './TableSortHead';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        minHeight: '200px',
        '@media (max-width:960px)': {
            width: 'calc(100vw - 80px)',
        },
    },
    row: {
        minHeight: '4rem',
    },
    name: {
        width: '20%',
    },
    email: {
        width: '20%',
        '&.MuiTableCell-body': {
            color: theme.palette.text.mainBlue,
        },
    },
    status: {
        width: '10%',
        '&.MuiTableCell-body': {
            color: '#212121',
            fontWeight: '600',
        },
    },
    roles: {
        width: '25%',
    },
    rolesColumn: {
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    lastLogin: {
        width: '22%',
    },
    actions: {
        width: '3%',
    },
    inactiveRow: {
        minHeight: '4rem',
        '& .MuiTableCell-body': {
            color: theme.palette.text.inactiveChildGrey,
            fontWeight: '400',
        },
    },
}));

const ASC = 'ASC';
const DESC = 'DESC';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_SORT_COLUMN = 'firstName';
const DEFAULT_SORT_TYPE = ASC;

const ChildAccountsTable = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const error = useSelector(errorSlice);
    const childAccounts = useSelector(childAccountsSlice);
    const loading = useSelector(loadingSlice);
    const selectedChildAccount = useSelector(childAccountSlice);
    const response = useSelector(requestResult);
    const me = useSelector(meSlice);
    const loadingAccountLockEditChild = useSelector(loadingAccountLockEditChildSlice);
    const accountLocked = useSelector(accountLockedSlice);

    const { impersonatedUser: readOnly } = me || {};

    const { content, totalElements, size, number } = childAccounts || {};

    const [sortProps, setSortProps] = useState({ sortColumn: null, sortType: ASC });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionProps, setActionProps] = useState({ action: 'activate', account: null });
    const [dialog, setDialog] = useState({
        title: '',
        visibility: false,
        message: '',
        onConfirmation: null,
        confirmButtonText: null,
        onCancel: null,
    });

    useEffect(() => {
        const sortColumn = DEFAULT_SORT_COLUMN;
        const sortType = DEFAULT_SORT_TYPE;
        const params = { page: DEFAULT_PAGE_NUMBER, size: DEFAULT_PAGE_SIZE, sortColumn, sortType };
        setSortProps({ sortColumn, sortType });
        dispatch({ type: `${SLICE_NAME}/requestChildAccounts`, payload: { params } });
    }, [dispatch]);

    useEffect(() => {
        if (response && response.ok) {
            const { fromAction } = response;
            if (fromAction === 'activateChildAccount') {
                dispatch({
                    type: `${NOTIFICATIONS_SLICE_NAME}/addNotification`,
                    payload: { message: t('child.account.activate.success') },
                });
            }
            if (fromAction === 'deactivateChildAccount') {
                dispatch({
                    type: `${NOTIFICATIONS_SLICE_NAME}/addNotification`,
                    payload: { message: t('child.account.deactivate.success') },
                });
            }
            dispatch({ type: `${SLICE_NAME}/requestResult`, payload: null });
            dispatch({
                type: `${SLICE_NAME}/requestChildAccounts`,
                payload: { params: { size, page: number, ...sortProps } },
            });
        }
    }, [response, dispatch, size, number, sortProps]);

    const handleCloseDialog = () => {
        setDialog({ title: '', visibility: false, message: '', onConfirmation: null, confirmButtonText: null, onCancel: null });
        dispatch({ type: `${SLICE_NAME}/error`, payload: false });
    };

    const editAccount = (childAccount) => {
        dispatch({ type: `${SLICE_NAME}/selectedChildAccount`, payload: childAccount });
        dispatch({ type: `${SLICE_NAME}/lockAccountDetails`, payload: { fromCard: 'EditChild', childUserName: childAccount.email } });
    };

    const resetDialog = () => {
        setActionProps({ title: '', description: '', account: null });
        setDialogOpen(false);
    };

    const setStatus = (childAccount) => {
        const { email, status } = childAccount;
        const action = status === 'Active' ? 'deactivate' : 'activate';
        const loggedInUser = me.email || me.username;
        if (email === loggedInUser && action === 'deactivate') {
            history.push('/my-account/preferences');
        } else {
            setActionProps({ action, account: childAccount });
            setDialogOpen(true);
        }
    };

    const updateStatus = () => {
        if (actionProps && actionProps.account) {
            const { email, parentUserName } = actionProps.account;
            dispatch({ type: `${SLICE_NAME}/${actionProps.action}ChildAccount`, payload: { userName: email, parentUserName: parentUserName } });
        }
        setDialogOpen(false);
    };

    const cancelUpdateStatus = () => {
        resetDialog();
    };

    const setSorting = (sortColumn) => {
        const isSameKey = sortColumn === sortProps.sortColumn;
        const oppositeSort = sortProps.sortType === ASC ? DESC : ASC;
        const sortType = !isSameKey ? ASC : oppositeSort;
        setSortProps({ sortColumn, sortType });
        const params = { sortColumn, sortType, size, page: DEFAULT_PAGE_NUMBER };
        dispatch({ type: `${SLICE_NAME}/requestChildAccounts`, payload: { params } });
    };

    const handleChangePage = (event, newPage) => {
        event.preventDefault();
        if (newPage !== number) {
            const params = { size, ...sortProps, page: newPage };
            dispatch({ type: `${SLICE_NAME}/requestChildAccounts`, payload: { params } });
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const pageSize = event.target.value;
        if (size !== pageSize) {
            const params = { size: pageSize, ...sortProps, page: 0 };
            dispatch({ type: `${SLICE_NAME}/requestChildAccounts`, payload: { params } });
        }
    };

    useEffect(() => {
        if (!loadingAccountLockEditChild && !accountLocked && selectedChildAccount) {
            history.push('/child-accounts/edit');
            dispatch({ type: `${SLICE_NAME}/accountLocked`, payload: null });
        }
    }, [loadingAccountLockEditChild]);

    useEffect(() => {
        if (!!error) {
            setDialog({
                ...dialog,
                visibility: true,
                message: t(error.description, { username: error.details.username, message: error.details.message }),
                onConfirmation: handleCloseDialog,
                confirmButtonText: t('ok'),
            });
        }
    }, [error]);

    const tableHead = (
        <TableSortHead
            classes={classes}
            setSorting={setSorting}
            sortBy={sortProps.sortColumn}
            sortValue={sortProps.sortType.toLowerCase()}
            readOnly={readOnly}
        />
    );

    const customPagination = (
        <CustomPagination
            defaultPageSize={DEFAULT_PAGE_SIZE}
            totalElements={totalElements}
            size={size || DEFAULT_PAGE_SIZE}
            page={number || DEFAULT_PAGE_NUMBER}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            paginationId={'childAccounts'}
        />
    );

    return (
        <React.Fragment>
            {!loading ? (
                <TableContainer className={classes.tableContainer}>
                    <Table className={classes.table} aria-label="simple table">
                        {tableHead}
                        <TableBody>
                            {(!content || content.length === 0) && (
                                <TableRow className={classes.row}>
                                    <TableCell colSpan={6} align="center">
                                        {t('child.account.not.associated')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {(content || []).map((child, chIdx) => {
                                const rowClass = child.status === 'Inactive' ? classes.inactiveRow : classes.row;
                                return (
                                    <TableRow key={`${child.email}_${chIdx}`} className={rowClass}>
                                        <TableCell className={classes.name} align="left">
                                            {child.firstName} {child.lastName}
                                        </TableCell>
                                        <TableCell className={classes.email} align="left">
                                            {child.email}
                                        </TableCell>
                                        <TableCell className={classes.status} align="left">
                                            {t(`child.status.${child.status}`)}
                                        </TableCell>
                                        <TableCell className={classes.roles} align="left">
                                            <div className={classes.rolesColumn}>
                                                {(child.roles || '').split(',').map((r, idx) => (
                                                    <span key={`${r}_${idx}`}>{t(`role.${r}`)}</span>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.lastLogin} align="left">
                                            {(child.lastLoginDateTime && bareDateTimeFormat(child.lastLoginDateTime)) || ''}
                                        </TableCell>
                                        {!readOnly && (
                                            <TableCell className={classes.actions} align="left">
                                                <ActionsButton account={child} edit={editAccount} setStatus={setStatus} />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {customPagination}
                </TableContainer>
            ) : (
                <CircularProgress />
            )}
            <ConfirmStatusChangeDialog
                open={dialogOpen}
                onAccept={updateStatus}
                onCancel={cancelUpdateStatus}
                action={actionProps.action}
                childAccount={actionProps.account || {}}
            />
            <WarningModal
                title={dialog.title}
                visibility={dialog.visibility}
                message={dialog.message}
                onConfirmation={dialog.onConfirmation}
                confirmButtonText={dialog.confirmButtonText}
                onCancel={dialog.onCancel}
            />
        </React.Fragment>
    );
};

export default ChildAccountsTable;
